import React, { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  CreditScoreBar,
  MaskedValue,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useCitation, getCitationForField } from "./CitationContext";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

// Fields to hide from display (backend-only)
const HIDDEN_FIELDS = new Set(["recording_date", "recording_number", "deed_type"]);

// Fields that should use currency formatting
const CURRENCY_FIELDS = new Set([
  "balance",
  "credit_limit",
  "monthly_payment",
  "amount",
  "sum",
  "total_outstanding_debt",
  "value",
  "estimated_value",
  "original_amount",
]);

// Words that should be all caps
const ALL_CAPS_WORDS = new Set(["ssn", "vin", "ein", "id", "dob", "ira", "agi"]);

// Convert snake_case or camelCase to Title Case
function formatLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      if (ALL_CAPS_WORDS.has(lower)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

// Check if a key should use currency formatting
function isCurrencyField(key: string): boolean {
  const keyLower = key.toLowerCase();
  return (
    CURRENCY_FIELDS.has(keyLower) ||
    keyLower.includes("balance") ||
    keyLower.includes("amount") ||
    keyLower.includes("payment") ||
    keyLower.includes("income") ||
    keyLower.includes("tax") ||
    keyLower.includes("debt")
  );
}

// Check if a field should be masked (SSN, EIN, etc.)
function isMaskedField(key: string): boolean {
  const keyLower = key.toLowerCase();
  return keyLower === "ssn" || keyLower === "ein" || keyLower.includes("social_security");
}

function getMaskFormat(key: string): "ssn" | "ein" {
  return key.toLowerCase() === "ein" ? "ein" : "ssn";
}


// Format a value for display
function formatValue(value: JsonValue, key: string): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    if (isCurrencyField(key)) {
      return formatCurrency(value);
    }
    const noFormatKeys = ["year", "vin", "score"];
    const keyLower = key.toLowerCase();
    if (noFormatKeys.some((k) => keyLower.includes(k))) {
      return value.toString();
    }
    return value.toLocaleString();
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{4}-\d{2}$/.test(value)) {
      return formatDate(value);
    }
    return value;
  }

  return "";
}

// Check if an object has any displayable values
function hasDisplayableContent(obj: JsonObject): boolean {
  return Object.entries(obj).some(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "object" && !Array.isArray(value)) {
      return hasDisplayableContent(value as JsonObject);
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  });
}

function isPrimitiveDisplayable(key: string, value: JsonValue, checkHidden = true): boolean {
  const isHidden = checkHidden && HIDDEN_FIELDS.has(key);
  const isPrimitive = value != null && typeof value !== "object";
  return !isHidden && isPrimitive;
}

// Special renderer for credit_scores array
function CreditScoresRenderer({ scores }: { scores: JsonArray }) {
  const validScores = scores.filter(
    (s): s is JsonObject =>
      typeof s === "object" &&
      s !== null &&
      !Array.isArray(s) &&
      typeof (s as JsonObject).score === "number" &&
      ((s as JsonObject).score as number) > 0,
  );

  if (validScores.length === 0) return null;

  const ficoScore = validScores.find((s) => {
    const model = String(s.score_model || "").toLowerCase();
    const source = String(s.source || "").toLowerCase();
    return model.includes("fico") || source.includes("fico");
  });
  const displayScore = ficoScore || validScores[0];
  const score = displayScore.score as number;

  return <CreditScoreBar score={score} />;
}

// Render flat object properties as divider rows
function FlatObjectRows({
  obj,
  citations,
  pathPrefix,
  onFieldClick,
  decryptedValues,
}: {
  obj: JsonObject;
  citations?: Record<string, unknown> | null;
  pathPrefix?: string;
  onFieldClick?: (fieldPath: string) => void;
  decryptedValues?: Record<string, string>;
}) {
  const entries = Object.entries(obj).filter(([key, value]) => isPrimitiveDisplayable(key, value));

  return (
    <>
      {entries.map(([key, value]) => {
        const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        const hasCitation = !!getCitationForField(citations ?? null, fieldPath);
        if (isMaskedField(key) && typeof value === "string") {
          const decrypted = decryptedValues?.[fieldPath];
          return (
            <InsightsDividerRow
              key={key}
              label={formatLabel(key)}
              value={
                decrypted === undefined ? (
                  <span className="text-stone-400">Loading...</span>
                ) : (
                  <MaskedValue value={decrypted || null} format={getMaskFormat(key)} />
                )
              }
              onClick={hasCitation && onFieldClick ? () => onFieldClick(fieldPath) : undefined}
            />
          );
        }
        const formatted = formatValue(value, key);
        if (!formatted) return null;
        return (
          <InsightsDividerRow
            key={key}
            label={formatLabel(key)}
            value={formatted}
            onClick={hasCitation && onFieldClick ? () => onFieldClick(fieldPath) : undefined}
          />
        );
      })}
    </>
  );
}

// Render array of objects - each object gets its own set of rows
function ArrayObjectRows({
  items,
  label,
  citations,
  arrayKey,
  onFieldClick,
}: {
  items: JsonArray;
  label: string;
  citations?: Record<string, unknown> | null;
  arrayKey?: string;
  onFieldClick?: (fieldPath: string) => void;
}) {
  const objectItems = items.filter(
    (item): item is JsonObject => typeof item === "object" && item !== null && !Array.isArray(item),
  );

  if (objectItems.length === 0) return null;

  return (
    <>
      {/* Section header */}
      <div className="py-2.5 px-4 bg-stone-50">
        <span className="text-sm font-medium text-stone-700">{label}</span>
      </div>
      {/* Items */}
      {objectItems.map((obj, index) => (
        <React.Fragment key={index}>
          {Object.entries(obj)
            .filter(([key, value]) => isPrimitiveDisplayable(key, value, false))
            .map(([key, value]) => {
              const fieldPath = arrayKey ? `${arrayKey}.${index}.${key}` : key;
              const hasCitation = !!getCitationForField(citations ?? null, fieldPath);
              const formatted = formatValue(value, key);
              if (!formatted) return null;
              return (
                <InsightsDividerRow
                  key={`${index}-${key}`}
                  label={formatLabel(key)}
                  value={formatted}
                  onClick={hasCitation && onFieldClick ? () => onFieldClick(fieldPath) : undefined}
                />
              );
            })}
        </React.Fragment>
      ))}
    </>
  );
}

// Render a section (top-level object) as a card
function SectionCard({
  title,
  obj,
  citations,
  sectionKey,
  onFieldClick,
  decryptedValues,
}: {
  title: string;
  obj: JsonObject;
  citations?: Record<string, unknown> | null;
  sectionKey?: string;
  onFieldClick?: (fieldPath: string) => void;
  decryptedValues?: Record<string, string>;
}) {
  // Separate primitives from arrays and nested objects
  const primitiveEntries = Object.entries(obj).filter(([key, value]) => {
    if (HIDDEN_FIELDS.has(key)) return false;
    if (value === null || value === undefined) return false;
    if (typeof value === "object") return false;
    return true;
  });

  const arrayEntries = Object.entries(obj).filter(([, value]) => {
    return Array.isArray(value) && value.length > 0;
  });

  const nestedObjectEntries = Object.entries(obj).filter(([, value]) => {
    return (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      hasDisplayableContent(value as JsonObject)
    );
  });

  const hasContent =
    primitiveEntries.length > 0 || arrayEntries.length > 0 || nestedObjectEntries.length > 0;

  if (!hasContent) return null;

  return (
    <InsightsCard title={title} noPadding>
      <InsightsDividerList>
        {/* Primitive fields */}
        {primitiveEntries.map(([key, value]) => {
          const fieldPath = sectionKey ? `${sectionKey}.${key}` : key;
          const hasCitation = !!getCitationForField(citations ?? null, fieldPath);
          if (isMaskedField(key) && typeof value === "string") {
            const decrypted = decryptedValues?.[fieldPath];
            return (
              <InsightsDividerRow
                key={key}
                label={formatLabel(key)}
                value={
                  decrypted === undefined ? (
                    <span className="text-stone-400">Loading...</span>
                  ) : (
                    <MaskedValue value={decrypted || null} format={getMaskFormat(key)} />
                  )
                }
                onClick={hasCitation && onFieldClick ? () => onFieldClick(fieldPath) : undefined}
              />
            );
          }
          const formatted = formatValue(value, key);
          if (!formatted) return null;
          return (
            <InsightsDividerRow
              key={key}
              label={formatLabel(key)}
              value={formatted}
              onClick={hasCitation && onFieldClick ? () => onFieldClick(fieldPath) : undefined}
            />
          );
        })}

        {/* Nested objects - flatten into rows */}
        {nestedObjectEntries.map(([key, value]) => (
          <React.Fragment key={key}>
            <div className="py-2.5 px-4 bg-stone-50">
              <span className="text-sm font-medium text-stone-700">{formatLabel(key)}</span>
            </div>
            <FlatObjectRows
              obj={value as JsonObject}
              citations={citations}
              pathPrefix={sectionKey ? `${sectionKey}.${key}` : key}
              onFieldClick={onFieldClick}
              decryptedValues={decryptedValues}
            />
          </React.Fragment>
        ))}

        {/* Arrays */}
        {arrayEntries.map(([key, value]) => (
          <ArrayObjectRows
            key={key}
            items={value as JsonArray}
            label={formatLabel(key)}
            citations={citations}
            arrayKey={sectionKey ? `${sectionKey}.${key}` : key}
            onFieldClick={onFieldClick}
          />
        ))}
      </InsightsDividerList>
    </InsightsCard>
  );
}

interface DynamicDocumentInsightsProps {
  data: Record<string, unknown>;
  citations?: Record<string, unknown> | null;
}

export default function DynamicDocumentInsights({ data, citations }: DynamicDocumentInsightsProps) {
  const { showCitation } = useCitation();
  const { person } = useAppContext();
  const [decryptedValues, setDecryptedValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setDecryptedValues({});
    if (!data || !person?.estate_id) return;

    const maskedEntries: [string, string][] = [];
    const collectMasked = (obj: Record<string, unknown>, prefix?: string) => {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        if (isMaskedField(key) && typeof value === "string" && value.length > 0) {
          maskedEntries.push([fullPath, value]);
        } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          collectMasked(value as Record<string, unknown>, fullPath);
        }
      }
    };
    collectMasked(data);

    if (maskedEntries.length === 0) return;

    let cancelled = false;
    const decryptAll = async () => {
      const results: Record<string, string> = {};
      for (const [key, value] of maskedEntries) {
        try {
          results[key] = await decryptPersonData(person.estate_id, value, key);
        } catch {
          results[key] = "";
        }
      }
      if (!cancelled) setDecryptedValues(results);
    };

    decryptAll();
    return () => { cancelled = true; };
  }, [data, person?.estate_id]);

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations ?? null, fieldPath);
    if (citation) showCitation(citation);
  };
  if (!data || typeof data !== "object") {
    return null;
  }

  // Special handling for credit_scores
  const creditScores = data.credit_scores as JsonArray | undefined;
  const hasCreditScores = Array.isArray(creditScores) && creditScores.length > 0;

  // Get all top-level keys that are objects (these become cards)
  const sections = Object.entries(data).filter(([key, value]) => {
    if (key === "credit_scores") return false;
    if (HIDDEN_FIELDS.has(key)) return false;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return false;
    }
    return hasDisplayableContent(value as JsonObject);
  });

  // Get top-level primitives (these go in a general card)
  const topLevelPrimitives = Object.entries(data).filter(([key, value]) => {
    if (HIDDEN_FIELDS.has(key)) return false;
    if (key === "credit_scores") return false;
    if (value === null || value === undefined) return false;
    if (typeof value === "object") return false;
    return true;
  });

  // Get top-level arrays (these become their own cards or sections)
  const topLevelArrays = Object.entries(data).filter(([key, value]) => {
    if (key === "credit_scores") return false;
    return Array.isArray(value) && value.length > 0;
  });

  if (
    sections.length === 0 &&
    topLevelPrimitives.length === 0 &&
    topLevelArrays.length === 0 &&
    !hasCreditScores
  ) {
    return null;
  }

  return (
    <div className="px-6 pb-6 space-y-4">
      {/* Credit Score card */}
      {hasCreditScores && (
        <InsightsCard title="Credit Score">
          <CreditScoresRenderer scores={creditScores} />
        </InsightsCard>
      )}

      {/* General Information card for top-level primitives */}
      {topLevelPrimitives.length > 0 && (
        <InsightsCard title="General Information" noPadding>
          <InsightsDividerList>
            {topLevelPrimitives.map(([key, value]) => {
              const hasCitation = !!getCitationForField(citations ?? null, key);
              if (isMaskedField(key) && typeof value === "string") {
                const decrypted = decryptedValues[key];
                return (
                  <InsightsDividerRow
                    key={key}
                    label={formatLabel(key)}
                    value={
                      decrypted === undefined ? (
                        <span className="text-stone-400">Loading...</span>
                      ) : (
                        <MaskedValue value={decrypted || null} format={getMaskFormat(key)} />
                      )
                    }
                    onClick={hasCitation ? () => handleFieldClick(key) : undefined}
                  />
                );
              }
              const formatted = formatValue(value as JsonValue, key);
              if (!formatted) return null;
              return (
                <InsightsDividerRow
                  key={key}
                  label={formatLabel(key)}
                  value={formatted}
                  onClick={hasCitation ? () => handleFieldClick(key) : undefined}
                />
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* Top-level arrays as cards */}
      {topLevelArrays.map(([key, value]) => {
        const items = value as JsonArray;
        const firstItem = items[0];

        // If array of objects, render as card with rows
        if (typeof firstItem === "object" && firstItem !== null && !Array.isArray(firstItem)) {
          return (
            <InsightsCard key={key} title={formatLabel(key)} noPadding>
              <InsightsDividerList>
                {items
                  .filter(
                    (item): item is JsonObject =>
                      typeof item === "object" && item !== null && !Array.isArray(item),
                  )
                  .map((obj, index) => (
                    <React.Fragment key={index}>
                      {Object.entries(obj)
                        .filter(([k, v]) => isPrimitiveDisplayable(k, v, false))
                        .map(([k, v]) => {
                          const fieldPath = `${key}.${index}.${k}`;
                          const hasCitation = !!getCitationForField(citations ?? null, fieldPath);
                          const formatted = formatValue(v, k);
                          if (!formatted) return null;
                          return (
                            <InsightsDividerRow
                              key={`${index}-${k}`}
                              label={formatLabel(k)}
                              value={formatted}
                              onClick={hasCitation ? () => handleFieldClick(fieldPath) : undefined}
                            />
                          );
                        })}
                    </React.Fragment>
                  ))}
              </InsightsDividerList>
            </InsightsCard>
          );
        }

        // Array of primitives
        const hasCitation = !!getCitationForField(citations ?? null, key);
        return (
          <InsightsCard key={key} title={formatLabel(key)} noPadding>
            <InsightsDividerList>
              <InsightsDividerRow
                label="Values"
                value={items
                  .filter((item) => item !== null && item !== undefined)
                  .map((item) => String(item))
                  .join(", ")}
                onClick={hasCitation ? () => handleFieldClick(key) : undefined}
              />
            </InsightsDividerList>
          </InsightsCard>
        );
      })}

      {/* Section cards */}
      {sections.map(([key, value]) => (
        <SectionCard
          key={key}
          title={formatLabel(key)}
          obj={value as JsonObject}
          citations={citations}
          sectionKey={key}
          onFieldClick={handleFieldClick}
          decryptedValues={decryptedValues}
        />
      ))}
    </div>
  );
}
